import React, {useEffect, useState} from 'react'
import {
	ApiRequestFailedEventArgs,
	Customer,
	Flight,
	JourneyContent,
	PaymentContent,
	RefundableProvider,
	SaleData,
	useChangeEventArgs,
	useQuoteRequestStatus,
	useRefundableActions
} from 'protect-group-pulse-react'
import {v4 as uuid} from 'uuid'
import {protectPlusOptions} from '../data'
import FormInput from '../components/ui/FormInput'
import moment from 'moment/moment'
import Button from '../components/ui/Button'
import EventArg from '../components/ui/EventArg'
import {ProtectPlusProvider, useProtectPlusContext} from '../components/ProtectPlus/ProtectPlusProvider'
import {LoadingShim} from '../components/ui/LoadingShim'
import {Action} from '../components/ProtectPlus/types'

export default function ProtectPlus() {
	return (
		<RefundableProvider options={protectPlusOptions}>
			<ProtectPlusProvider>
				<div className="p-5">
					<div className="flex items-center justify-between mb-5 w-full">
						<h1 className="font-medium text-3xl text-pulse-blue">Protect Plus Product Demo</h1>
						<a className="font-medium text-xl text-pulse-blue" href="/">Home</a>
					</div>
					<div className="flex justify-between">
						<div className="pr-3 w-1/2">
							<div className="border border-pulse-blue p-3 rounded">
								<div className="pb-3">
									<p className="font-medium text-pulse-blue">My Booking</p>
									<QuoteInputs/>
								</div>
								<div className="border-t border-pulse-blue pt-3">
									<p className="font-medium text-pulse-blue pb-3">My Basket</p>
									<Basket/>
								</div>
							</div>
							<div className="mt-5">
								<Refundable/>
							</div>
							<div className="mt-5">
								<SaleInputs/>
							</div>
						</div>
						<div className="pl-3 w-1/2">
							<div>
								<JourneyContent/>
							</div>
						</div>
					</div>
				</div>
			</ProtectPlusProvider>
		</RefundableProvider>
	)
}

function Refundable() {
	const {dispatch} = useProtectPlusContext()
	const handleApiError = (args: ApiRequestFailedEventArgs) => {
		dispatch({type: Action.quoteError, value: args})
	}

	return (
		<PaymentContent onApiError={handleApiError}/>
	)
}

function Basket() {
	const eventArgs = useChangeEventArgs()
	const {dispatch, state: {quoteError}} = useProtectPlusContext()

	useEffect(() => {
		dispatch({type: Action.quoteError, value: null})
		console.log('change event args', eventArgs)
	}, [eventArgs])

	return (
		<div>
			{
				(eventArgs && !quoteError) && (
					<div>
						{
							(eventArgs.products ?? [])
								.filter(product => product.sold === true)
								.map((product) => {
									return (
										<div key={product.productId}>
											<EventArg label={product.label} value={product.formattedValue}/>
										</div>
									)
								})
						}
						<EventArg label="Products" value={eventArgs.formattedProductValue}/>
						<EventArg label="Total" value={eventArgs.formattedTotalValue}/>
						<EventArg label="Quote Id" value={eventArgs.quoteId}/>
					</div>
				)
			}
			{
				(quoteError) && (
					<div>
						<p className="text-red-400">{JSON.stringify(quoteError.error)}</p>
					</div>
				)
			}
		</div>
	)
}

const parseDate = (value: string) => moment(value, 'yyyy-MM-DD').format(protectPlusOptions.eventDateFormat)

function QuoteInputs() {
	const {dispatch, state: {quoteData}} = useProtectPlusContext()
	const {updateQuoteData} = useRefundableActions()
	const status = useQuoteRequestStatus()

	useEffect(() => {
		updateQuoteData(quoteData)
	}, [])

	const eventDate = moment(quoteData.eventTravelDateTime, protectPlusOptions.eventDateFormat).format('yyyy-MM-DD')

	const handleOptionsChange = (option: string, parse?: (val: any) => any) => (event: any) => {
		let value = event.target.value

		if (parse) {
			value = parse(value)
		}

		dispatch({type: Action.updateQuoteData, value: {...quoteData, [option]: value}})
	}

	const handleFlightChange = (option: string) => (event: any) => {
		const value = event.target.value
		const flight: Flight = (quoteData.flights || [{}])[0] as Flight

		dispatch({type: Action.updateQuoteData, value: {
			...quoteData,
			flights: [
				{
					...flight,
					[option]: value
				}
			]}})
	}

	return (
		<div>
			<FormInput
				id="bookingCost"
				inputProps={{
					min: 0
				}}
				label="Booking Cost"
				onChange={handleOptionsChange('totalValue', parseFloat)}
				type="number"
				value={quoteData.totalValue}
			/>
			<FormInput
				id="eventDate"
				label="Event Date"
				onChange={handleOptionsChange('eventTravelDateTime', parseDate)}
				type="date"
				value={eventDate}
			/>
			<FormInput
				id="numberOfTickets"
				label="Tickets"
				onChange={handleOptionsChange('numberOfTickets', parseInt)}
				type="number"
				value={quoteData.numberOfTickets}
			/>
			<FormInput
				id="displayCurrency"
				label="Display Currency Code"
				onChange={handleOptionsChange('displayCurrencyCode')}
				value={quoteData.displayCurrencyCode}
			/>
			<FormInput
				id="departureAirportCode"
				label="Departure Airport Code"
				onChange={handleFlightChange('departureCode')}
				value={(quoteData.flights ?? [{}])[0].departureCode}
			/>
			<FormInput
				id="arrivalAirportCode"
				label="Arrival Airport Code"
				onChange={handleFlightChange('arrivalCode')}
				value={(quoteData.flights ?? [{}])[0].arrivalCode}
			/>
			<Button
				disabled={status === 'pending'}
				label="Update Quote"
				onClick={() => updateQuoteData(quoteData)}/>
		</div>
	)
}

function SaleInputs() {
	const {writeSale} = useRefundableActions()
	const [customer, setCustomer] = useState<Customer>({} as Customer)
	const [flight, setFlight] = useState<Flight>({} as Flight)
	const { dispatch, state: {saleError, saleResponse} } = useProtectPlusContext()

	const handleCustomerChange = (key: string) => (evt: any) =>
		setCustomer(curr => ({...curr, [key]: evt.target.value}))

	const handleFlightChange = (key: string, parse?: (val: any) => any) => (evt: any) => {
		let value = evt.target.value

		if (parse) {
			value = parse(value)
		}
		setFlight(curr => ({...curr, [key]: value}))
	}

	const handleClick = async () => {
		try {
			dispatch({type: Action.saleInProgress, value: true})
			const data: SaleData = {
				bookingReference: uuid(),
				customers: [customer],
				flights: [flight]
			}
			const response = await writeSale(data)
			dispatch({type: Action.saleComplete, value: response})
		} catch (e: any) {
			const { data } = e
			dispatch({type: Action.saleError, value: data})
		} finally {
			dispatch({type: Action.saleInProgress, value: false})
		}
	}

	return (
		<div className="border border-pulse-blue p-3 relative rounded">
			<LoadingShim/>
			<p className="font-medium text-pulse-blue pb-3">Checkout</p>
			<div>
				<div>
					<Customers customer={customer} onCustomerChange={handleCustomerChange}/>
				</div>
				<div>
					<Flights flight={flight} onFlightChange={handleFlightChange}/>
				</div>
				<div>
					<Button label="Pay Now" onClick={handleClick}/>
				</div>
			</div>
			{
				(saleResponse) && (
					<div className="border-t border-pulse-blue mt-3 pt-3">
						<p className="font-medium text-pulse-blue pb-3">Booking Confirmation</p>
						<div>
							<EventArg label="Sale Id" value={saleResponse.saleId}/>
							<EventArg label="Refundable Link" value={saleResponse.refundableLink}/>
						</div>
					</div>
				)
			}
			{
				(saleError) && (
					<div className="border-t border-pulse-blue mt-3 pt-3">
						<p className="text-red-400">{JSON.stringify(saleError)}</p>
					</div>
				)
			}
		</div>
	)
}

function Customers({customer, onCustomerChange}: {
	customer: Customer,
	onCustomerChange: (key: string) => (evt: any) => void
}) {
	return (
		<div>
			<p className="font-medium text-pulse-blue pb-3">Customer Info</p>
			<div>
				<FormInput
					id="firstName"
					label="First Name"
					onChange={onCustomerChange('firstName')}
					type="string"
					value={customer.firstName ?? ''}
				/>
				<FormInput
					id="lastName"
					label="Last Name"
					onChange={onCustomerChange('lastName')}
					type="string"
					value={customer.lastName ?? ''}
				/>
				<FormInput
					id="email"
					label="Email"
					onChange={onCustomerChange('email')}
					type="email"
					value={customer.email ?? ''}
				/>
				<FormInput
					id="phoneNumber"
					label="Phone Number"
					onChange={onCustomerChange('telephone')}
					type="tel"
					value={customer.telephone ?? ''}
				/>
			</div>
		</div>
	)
}

function Flights({flight, onFlightChange}: {
	flight: Flight,
	onFlightChange: (key: string, parse?: (val: any) => any) => (evt: any) => void
}) {

	const flightDate = moment(flight.departureDate, protectPlusOptions.eventDateFormat).format('yyyy-MM-DD')

	return (
		<div>
			<p className="font-medium text-pulse-blue pb-3">Flight Data</p>
			<div>
				<FormInput
					id="departureCode"
					label="Departure Airport Code"
					onChange={onFlightChange('departureCode')}
					type="string"
					value={flight?.departureCode ?? ''}
				/>
				<FormInput
					id="arrivalCode"
					label="Arrival Code"
					onChange={onFlightChange('arrivalCode')}
					type="string"
					value={flight?.arrivalCode ?? ''}
				/>
				<FormInput
					id="departureDate"
					label="Departure Date"
					onChange={onFlightChange('departureDate', parseDate)}
					type="date"
					value={flightDate}
				/>
				<FormInput
					id="flightNumber"
					label="Flight Number"
					onChange={onFlightChange('flightNumber')}
					type="string"
					value={flight?.flightNumber ?? ''}
				/>
			</div>
		</div>
	)
}
